/**
 * KISSED University · public application-status check.
 * Route: /university/status (id + email required to view)
 *
 * If status is `payment_pending`, surfaces a "Complete your payment" CTA that
 * starts a fresh Stripe Checkout session for the same application.
 */
import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, Search, CreditCard, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import logo from "@/assets/brand/kissed-logo.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";

const STATUS_LABEL = {
  payment_pending: "Awaiting payment",
  submitted: "Submitted · Awaiting review",
  under_review: "Under review",
  accepted: "Accepted",
  conditionally_accepted: "Conditionally accepted",
  waitlisted: "Waitlisted",
  declined: "Declined",
  enrolled: "Enrolled",
};

export default function UniversityStatus() {
  const [params] = useSearchParams();
  const [id, setId] = useState(params.get("id") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const fresh = params.get("fresh") === "1";

  const lookup = async (e) => {
    if (e) e.preventDefault();
    if (!id || !email) {
      toast.error("Both Application ID and email are required.");
      return;
    }
    setLoading(true);
    try {
      const r = await universityApi.status(id, email);
      setData(r.data);
    } catch (err) {
      if (err?.response?.status === 404) toast.error("No matching application found for that ID and email.");
      else toast.error(formatApiError(err, "Lookup failed"));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const retryPayment = async () => {
    setRetrying(true);
    try {
      const r = await universityApi.startCheckout(id, {
        application_id: id, email, origin_url: window.location.origin,
      });
      const url = r?.data?.url;
      if (r?.data?.already_paid) {
        toast.message("Your payment is already on file. Refreshing status…");
        await lookup();
        return;
      }
      if (!url) {
        toast.error("Could not start Square payment. Please contact admissions.");
        return;
      }
      window.location.href = url;
    } catch (err) {
      toast.error(formatApiError(err, "Could not start Square payment"));
    } finally {
      setRetrying(false);
    }
  };

  useEffect(() => {
    if (id && email) lookup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const programs = data ? [data.primary_program, data.secondary_program].filter(Boolean) : [];

  return (
    <div data-testid="university-status" className="min-h-screen bg-[#faf7f5]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/university"><img src={logo} alt="KISSED" className="h-11 w-auto drop-shadow-md" /></Link>
          <span className="font-display text-sm text-slate-700">Application status</span>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-6 py-10 space-y-6">
        {fresh && data?.current_status === "submitted" && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="status-fresh-banner">
            Thank you — payment received and your application is officially submitted. We'll email you a decision within 3–5 business days. Bookmark this page to check status anytime.
          </div>
        )}
        <form onSubmit={lookup} className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
          <h2 className="font-display text-xl text-slate-900">Check application status</h2>
          <p className="text-sm text-slate-600">Both your Application ID and the email you applied with are required.</p>
          <div><Label>Application ID</Label><Input data-testid="status-id" value={id} onChange={(e) => setId(e.target.value)} className="mt-2 font-mono" /></div>
          <div><Label>Email</Label><Input data-testid="status-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2" /></div>
          <div className="flex justify-end">
            <Button type="submit" disabled={loading} data-testid="status-lookup-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}Look up
            </Button>
          </div>
        </form>

        {data && (
          <div className="rounded-2xl border border-purple-200 bg-white p-5" data-testid="status-result">
            <div className="text-xs uppercase tracking-wide text-purple-900">Current status</div>
            <div className="mt-1 font-display text-2xl text-slate-900" data-testid="status-current">
              {STATUS_LABEL[data.current_status] || data.current_status}
            </div>

            {data.current_status === "payment_pending" && (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="status-payment-pending">
                <div className="flex gap-2 items-start">
                  <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium text-amber-900 text-sm">Your $50 application fee has not yet been received.</div>
                    <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                      A non-refundable $50 application fee is required to submit your application for admissions review. Your application is saved — click below to complete payment via Square. Your spot is not in the review queue until the fee is paid.
                    </p>
                    <Button
                      className="mt-3 bg-purple-900 hover:bg-purple-800"
                      disabled={retrying}
                      onClick={retryPayment}
                      data-testid="status-complete-payment-btn"
                    >
                      {retrying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1" />}
                      Pay via Square
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-4 text-sm text-slate-700 space-y-1">
              {data.public_application_id ? (
                <div data-testid="status-public-id">
                  Application ID: <span className="font-mono text-base font-semibold text-slate-900">{data.public_application_id}</span>
                </div>
              ) : (
                <div>Application ID: <span className="font-mono">{data.application_id}</span></div>
              )}
              {data.submitted_at ? <div>Submitted: {new Date(data.submitted_at).toLocaleDateString()}</div> : null}
              {data.decided_at ? <div>Decided: {new Date(data.decided_at).toLocaleDateString()}</div> : null}
              {programs.length ? <div>Programs: {programs.join(" + ")}</div> : null}
              {data.specialization_track ? <div>Specialization: {data.specialization_track}</div> : null}
              {data.application_fee_status ? (
                <div data-testid="status-fee-status">
                  Application fee: <span className="font-medium">{data.application_fee_status}</span>
                  {data.application_fee_payment_method ? <> · via {data.application_fee_payment_method}</> : null}
                  {data.application_fee_paid_date ? <> · {new Date(data.application_fee_paid_date).toLocaleDateString()}</> : null}
                </div>
              ) : null}
            </div>
            <p className="mt-4 text-xs text-slate-500">
              Decision emails are sent to the address on file. If you haven't received yours after 5 business days from submission, please contact admissions@kissedbycvon.com.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
